import { Check, ChevronsUpDown, X } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

import { Badge } from '~/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

export interface MultiSelectOption {
  /** The value stored in state when this option is selected. */
  value: string;
  /** Human-readable label shown in the dropdown and badge. */
  label: string;
  /** Overrides the selected-chip's colour classes (e.g. to match a status/impact badge elsewhere). Defaults to the primary chip style. */
  badgeClassName?: string;
  /** Muted trailing text in the dropdown row only (e.g. a count) — never on the selected badge. */
  meta?: string;
}

export interface MultiSelectProps {
  /** Available options to choose from. */
  options: MultiSelectOption[];
  /** Currently selected values (controlled). */
  value: string[];
  /** Called with the full new selection array on every change. */
  onChange: (value: string[]) => void;
  /** Placeholder shown in the trigger when nothing is selected. */
  placeholder?: string;
  /** Placeholder shown inside the Command search input. */
  searchPlaceholder?: string;
  /** Disables the trigger and prevents opening the dropdown. */
  disabled?: boolean;
  /** Extra classes applied to the trigger element. */
  className?: string;
  /** `data-testid` on the trigger; derives `-content`, `-search-input`, `-option-${value}`, `-chip-${value}`, `-clear-all`. */
  testId?: string;
  /** Accessible name for the trigger when no visible label is associated with it. */
  ariaLabel?: string;
  /** Replaces the selected-chip row in the trigger (e.g. "Subsidiary · 3") so a toolbar trigger
   *  keeps one line and a fixed width however many values are picked. Only called with ≥ 1
   *  selection; the placeholder still shows when nothing is selected. */
  renderValue?: (selected: MultiSelectOption[]) => ReactNode;
}

/**
 * A multi-select dropdown built on shadcn's Command + Popover primitives.
 *
 * Selected items appear as dismissible badges in the trigger.
 * The dropdown provides a search input to filter options.
 *
 * @example
 * ```tsx
 * const [selected, setSelected] = useState<string[]>([]);
 *
 * <MultiSelect
 *   options={[
 *     { value: 'google', label: 'Google' },
 *     { value: 'meta', label: 'Meta' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 *   placeholder="Select platforms…"
 * />
 * ```
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled,
  className,
  testId = 'multi-select',
  ariaLabel,
  renderValue,
}: MultiSelectProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);

  function toggle(optValue: string) {
    onChange(value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
  }

  function removeBadge(optValue: string, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optValue));
  }

  return (
    <Popover modal open={open} {...(!disabled && { onOpenChange: setOpen })}>
      {/* Trigger — div[role="combobox"] avoids nesting <button> inside <button> */}
      <PopoverTrigger asChild>
        <div
          role="combobox"
          data-testid={testId}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) setOpen(true);
          }}
          className={cn(
            'border-input bg-background data-[state=open]:ring-ring flex min-h-10 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm focus:outline-none data-[state=open]:ring-2 data-[state=open]:ring-offset-0',
            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
            className,
          )}
        >
          {/* Selected badges or placeholder */}
          <div className="flex min-w-0 flex-wrap gap-1">
            {value.length > 0 && renderValue ? (
              <span data-testid={`${testId}-value`} className="text-foreground min-w-0 truncate">
                {renderValue(
                  value.map((v) => options.find((o) => o.value === v) ?? { value: v, label: v }),
                )}
              </span>
            ) : value.length > 0 ? (
              value.map((v) => {
                const opt = options.find((o) => o.value === v);
                return (
                  <Badge
                    key={v}
                    data-testid={`${testId}-chip-${v}`}
                    variant={opt?.badgeClassName ? 'outline' : 'default'}
                    className={cn(
                      'flex items-center gap-1 pr-1',
                      opt?.badgeClassName ?? 'bg-primary text-white',
                    )}
                  >
                    {opt?.label ?? v}
                    <button
                      type="button"
                      data-testid={`${testId}-chip-${v}-remove-button`}
                      onClick={(e) => {
                        removeBadge(v, e);
                      }}
                      className={cn(
                        'ml-0.5 rounded-full',
                        opt?.badgeClassName
                          ? 'text-current/70 hover:text-current'
                          : 'text-white/80 hover:text-white',
                      )}
                      aria-label={`Remove ${opt?.label ?? v}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="text-muted-foreground ml-2 h-4 w-4 shrink-0" />
        </div>
      </PopoverTrigger>

      {/* Dropdown — constrained to available viewport height so the list scrolls
          correctly even when the trigger is near the bottom of the screen. */}
      <PopoverContent
        className="flex max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) flex-col overflow-hidden p-0"
        align="start"
        data-testid={`${testId}-content`}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <Command className="min-h-0 flex-1">
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search-input`} />
          <CommandList id={listId} className="min-h-0 flex-1">
            <CommandEmpty data-testid={`${testId}-empty`}>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`${testId}-option-${opt.value}`}
                  // Search matches the label too — an opaque value (an id) is not what people type.
                  keywords={[opt.label]}
                  onSelect={() => {
                    toggle(opt.value);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value.includes(opt.value) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {opt.label}
                  {opt.meta !== undefined && (
                    <span className="text-muted-foreground ml-auto pl-2 text-xs tabular-nums">
                      {opt.meta}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>

            {value.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    data-testid={`${testId}-clear-all`}
                    onSelect={() => {
                      onChange([]);
                    }}
                    className="text-muted-foreground justify-center text-xs"
                  >
                    Clear all
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
